import { FC, useEffect } from 'react'
import { Text } from '@revolut-internal/ui-kit'
import { v4 as uuidv4 } from 'uuid'
import { formatISO } from 'date-fns'
import { TZDate } from '@date-fns/tz'

import { ErrorWidget } from 'components'
import { useUser } from 'containers/App'
import { AuthorRole, GetFixedPromptTextGenerationOutState } from 'lib/apiClient'
import { getFullUserName } from 'lib/utils'
import {
  useAssistantChatMessagesContext,
  useGenerateAssistantChatMessage,
} from 'pages/AssistantPlayground/hooks'
import { createAssistantChatGenerationPayload } from 'pages/AssistantPlayground/utils/chatMessages'

import { ChatMessagesList } from '../ChatMessagesList'
import { ChatInput } from '../ChatInput'
import {
  AssistantPlaygroundContentWrapper,
  ChatContentWrapper,
} from './AssistantPlaygroundContent.style'
import { AssistantPlaygroundContentLoading } from './AssistantPlaygroundContentLoading'

interface AssistantPlaygroundContentProps {
  isLoading: boolean
  isError: boolean
  retry: () => void
}

export const AssistantPlaygroundContent: FC<AssistantPlaygroundContentProps> = props => {
  const { user } = useUser()

  const {
    addChatMessageToThread,
    previousSnapshotId,
    isLoading: isLoadingMessages,
    latestAssistantChatMessage,
    initialData,
    chatMessages,
  } = useAssistantChatMessagesContext()

  const { generateAssistantResponse, isLoading, isSubmitDisabled } =
    useGenerateAssistantChatMessage()

  useEffect(() => {
    if (initialData?.state === GetFixedPromptTextGenerationOutState.InProgress) {
      generateAssistantResponse()
    }
  }, [generateAssistantResponse, initialData])

  if (props.isLoading || isLoadingMessages) {
    return <AssistantPlaygroundContentLoading />
  }

  if (props.isError) {
    return <ErrorWidget description="Failed to load chat data" onRetry={props.retry} />
  }

  const handleSubmit = (inputValue: string) => {
    const id = uuidv4()

    addChatMessageToThread({
      messageId: id,
      text: inputValue,
      authorRole: AuthorRole.Customer,
      created: formatISO(TZDate.tz('UTC')),
    })

    const creationPayload = createAssistantChatGenerationPayload({
      id,
      text: inputValue,
      previousDialogSnapshotId: previousSnapshotId,
      latestAssistantChatMessage,
      authorName: getFullUserName(user),
      chatMessages,
    })

    generateAssistantResponse(creationPayload)
  }

  return (
    <AssistantPlaygroundContentWrapper>
      <ChatContentWrapper>
        <ChatMessagesList isGeneratingAssistantResponse={isLoading} />
        <ChatInput onCreateNewMessage={handleSubmit} submitDisabled={isSubmitDisabled} />
      </ChatContentWrapper>
      <Text variant="emphasis2" color="grey-50">
        Based on your Revolut account data · last updated yesterday
      </Text>
    </AssistantPlaygroundContentWrapper>
  )
}
